import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import { UsageEventsTracker } from '../../../services/UsageEventsTracker';
import { useToast } from '../../../contexts/ToastContext';
import { useSubscription, getUserTier } from '../../../contexts/SubscriptionContext';
import usePremiumAccess from '../../../hooks/usePremiumAccess';
import { AiStudyGenerationApi } from '../../../services/ai/AiStudyGenerationApi';
import { registerJob, completeJob, failJob } from '../../../hooks/useGenerationJobs';
import DocumentSelectionModal from '../../documents/DocumentSelectionModal';
import MiniGameLoadingOverlay from '../MiniGameLoadingOverlay';
import QuizHistoryPanel from '../../common/QuizHistoryPanel';
import QuizDetailView from '../../common/QuizDetailView';
import UpgradeModal from '../../common/UpgradeModal';
import './QuizzesTool.css';
import '../../primitives/Button.css';

// Module-level store for generation results that complete while component is unmounted.
// When the user switches tabs during quiz generation, the async handler writes here
// and the component picks up the result on remount.
let pendingGenerationResult = null;

// const scoutQuizzesIcon = '/scout_images/scout_quizzes.png'; // (not used)

/**
 * QuizBuilderPanel - Reusable quiz configuration form
 * Used in both CreateQuizCard (collapsed, compact) and expanded workspace (comfortable)
 */
const QuizBuilderPanel = ({ 
  selectedDocumentIds,
  numQuestions,
  difficultyLevel,
  questionTypePreset,
  loading,
  error,
  onSetShowDocModal,
  onSetNumQuestions,
  onSetDifficultyLevel,
  onSetQuestionTypePreset,
  onGenerateQuiz,
  density = 'comfortable' // 'compact' | 'comfortable'
}) => {
  const isCompact = density === 'compact';
  
  if (loading) {
    return (
      <div className="quiz-loading-state">
        <div className="loading-spinner" />
        <p>Generating quiz questions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-error">
        <div className="error-icon">⚠️</div>
        <h3>Generation Failed</h3>
        <p>{error}</p>
        <button 
          className="ui-btn ui-btn--primary"
          onClick={onGenerateQuiz}
        >
          Try Again
        </button>
      </div>
    );
  }

  const selectedTypes = new Set(questionTypePreset.split(','));

  const handleTypeToggle = (type) => {
    const next = new Set(selectedTypes);
    if (next.has(type)) next.delete(type); else next.add(type);
    if (next.size === 0) return;
    onSetQuestionTypePreset([...next].join(','));
  };

  return (
    <div className={`quiz-builder-panel ${isCompact ? 'quiz-builder-panel--compact' : ''}`}>
      <div className="quiz-settings">
        {/* Documents - status indicator when selected, prompt when empty */}
        {selectedDocumentIds.length > 0 ? (
          <div className="doc-status" onClick={() => onSetShowDocModal(true)}>
            <div className="doc-status__badge">
              <span className="doc-status__icon">📄</span>
              <span className="doc-status__count">{selectedDocumentIds.length}</span>
              <span className="doc-status__label">Document{selectedDocumentIds.length !== 1 ? 's' : ''} Selected</span>
            </div>
            <button className="doc-status__action">Change</button>
          </div>
        ) : (
          <button
            className="ui-btn ui-btn--neutral"
            onClick={() => onSetShowDocModal(true)}
          >
            📄 Select Documents
          </button>
        )}

        <div className="quiz-divider" />

        <div className="quiz-form-section">
          <span className="control-label">Questions</span>
          <input
            id="num-questions"
            type="range"
            min="5"
            max="50"
            step="5"
            value={numQuestions}
            onChange={(e) => onSetNumQuestions(Number(e.target.value))}
            className="quiz-slider"
          />
          <span className="control-value-below">{numQuestions} questions</span>
        </div>

        <div className="quiz-divider" />

        <div className="quiz-form-section">
          <div className="quiz-options-grid">
            <div className="difficulty-group">
              <div className="section-label">Difficulty</div>
              {['beginner', 'intermediate', 'advanced'].map(level => (
                <label key={level} className="radio-option">
                  <input
                    type="radio"
                    name="difficulty"
                    value={level}
                    checked={difficultyLevel === level}
                    onChange={() => onSetDifficultyLevel(level)}
                  />
                  {level.charAt(0).toUpperCase() + level.slice(1)}
                </label>
              ))}
            </div>

            <div className="question-type-group">
              <div className="section-label">Question Types</div>
              <span className="section-hint">Select one or more</span>
              {[
                { value: 'mc', label: 'Multiple Choice' },
                { value: 'tf', label: 'True / False' },
                { value: 'sa', label: 'Short Answer' }
              ].map(type => (
                <label key={type.value} className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={selectedTypes.has(type.value)}
                    onChange={() => handleTypeToggle(type.value)}
                  />
                  {type.label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="quiz-divider" />

        <div className="quiz-form-section quiz-form-section--cta">
          <button
            className="quiz-generate-btn"
            onClick={onGenerateQuiz}
            disabled={selectedDocumentIds.length === 0 || loading}
          >
            {loading ? 'Generating...' : '✨ Generate Quiz'}
          </button>
          {selectedDocumentIds.length === 0 && (
            <span className="quiz-generate-hint">Select documents to enable quiz generation</span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Format timestamp to friendly relative time (e.g., "4h ago", "2d ago")
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Friendly time string
 */
// formatRelativeTime removed (not used)

/**
 * Get Scout's reaction based on quiz score percentage
 * @param {number} percentCorrect - Percentage of correct answers (0-100)
 * @returns {Object} Scout state with imageSrc, headline, and message
 */
function getScoutSummaryState(percentCorrect) {
  if (percentCorrect >= 75) {
    return {
      imageSrc: "/scout_images/quizzes_scout_goodjob.png",
      headline: "Nice work!",
      message: "That's a strong round. Want to run it back and hit an even higher score?"
    };
  }
  if (percentCorrect >= 50) {
    return {
      imageSrc: "/scout_images/scout_thinking.png",
      headline: "Solid progress.",
      message: "You're close. Review the misses and try another quiz—this is where the gains happen."
    };
  }
  if (percentCorrect >= 25) {
    return {
      imageSrc: "/scout_images/scout_scratching_head.png",
      headline: "Hmm… we've got some gaps.",
      message: "No stress. Let's review answers and do a fresh quiz right after—momentum wins."
    };
  }
  return {
    imageSrc: "/scout_images/scout_tryagain.png",
    headline: "Let's reset.",
    message: "This one didn't land yet—but that's fine. Review answers and take another swing right now."
  };
}

/**
 * QuizzesTool - AI-powered quiz generation with step-by-step taking flow
 * 
 * Features:
 * - Multi-document selection via DocumentSelectionModal
 * - Configurable quiz parameters (num_questions, difficulty_level, question types)
 * - Step-by-step quiz taking with progress tracking
 * - Confidence-based short answer grading
 * - Keyboard navigation support
 * - Full state persistence for seamless tool switching
 * - Theme token-based styling for automatic theme updates
 */
export default function QuizzesTool({ 
  selectedCourse, 
  persistedState,
  onStateChange,
  isPanelExpanded = false,
  isCollapsed = false // Passed from parent to detect collapsed desk state
}) {
  const { user } = useAuth();
  const dataContext = useData();
  const { startQuizAttempt, submitQuizAnswers, getAttemptResults, invalidateQuizAttemptCaches, getQuizSet, getDocumentById } = dataContext || {};
  const { addToast } = useToast();
  const { subscription, initialized } = useSubscription();
  const { canCreate, tier: currentTier, initialized: premiumInitialized, previewGating } = usePremiumAccess();
  const containerRef = useRef(null);
  
  // Subscription gating state – blocks NEW creation, not history viewing
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const isCreationBlocked = (initialized || premiumInitialized) && !canCreate('quizzes');

  // TEMP: debug logging — remove when rolling out premium gating
  useEffect(() => {
    console.log('[QuizzesTool] Gating state:', {
      initialized,
      premiumInitialized,
      previewGating,
      canCreateQuizzes: canCreate('quizzes'),
      isCreationBlocked,
    });
  }, [initialized, premiumInitialized, previewGating, isCreationBlocked, canCreate]);

  // All documents are now selectable — AI service handles both RAG-processed and unprocessed docs
  const requiredStatusIds = [];
  const prevStateRef = useRef(null);
  const activeRequestRef = useRef(null); // { controller, requestId }
  const isMountedRef = useRef(true);
  const isGeneratingRef = useRef(false);
  const lastConsumedExternalQuizRef = useRef(null);
  const cardTopRef = useRef(null);
  const onStateChangeRef = useRef(onStateChange);

  // ============ PERSISTED STATE ============
  // Initialize from persisted state or defaults
  const [selectedDocumentIds, setSelectedDocumentIds] = useState(
    persistedState?.selectedDocumentIds || []
  );
  const [numQuestions, setNumQuestions] = useState(
    persistedState?.numQuestions || 20
  );
  const [difficultyLevel, setDifficultyLevel] = useState(
    persistedState?.difficultyLevel || 'intermediate'
  );
  const [questionTypePreset, setQuestionTypePreset] = useState(() => {
    const saved = persistedState?.questionTypePreset;
    if (!saved) return 'mc,tf,sa';
    // Migrate old preset strings to new comma format
    if (saved === 'mixed') return 'mc,tf,sa';
    if (saved === 'multiple_choice_only' || saved === 'mcq-heavy') return 'mc';
    if (saved === 'short_answer_only' || saved === 'short-answer') return 'sa';
    return saved; // already new format
  });
  const [quizResult, setQuizResult] = useState(
    persistedState?.quizResult || null
  );
  const [loading, setLoading] = useState(
    // Restore loading state from parent — if generation was in-flight when we unmounted,
    // show loading UI on remount (pending result will be consumed from module-level store)
    persistedState?.loading || false
  );
  const [isGrading, setIsGrading] = useState(false);
  const [error, setError] = useState(null);
  
  // View mode: "list" (quiz history) or "builder" (quiz creation/taking)
  const [viewMode, setViewMode] = useState(
    persistedState?.viewMode || (persistedState?.quizResult ? 'builder' : 'list')
  );
  
  // Collapsed mode UI state (not persisted)
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const listContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const [overlayHeight, setOverlayHeight] = useState(0);

  // ============ QUIZ SESSION STATE (PERSISTED) ============
  const [attemptId, setAttemptId] = useState(
    persistedState?.attemptId || null
  );
  const [mode, setMode] = useState(
    persistedState?.mode || (persistedState?.quizResult ? 'taking' : 'setup')
  );
  const [currentIndex, setCurrentIndex] = useState(
    persistedState?.currentIndex ?? 0
  );
  const [answersByIndex, setAnswersByIndex] = useState(
    persistedState?.answersByIndex || {}
  );
  const [submitted, setSubmitted] = useState(
    persistedState?.submitted || false
  );
  const [scoreBreakdown, setScoreBreakdown] = useState(
    persistedState?.scoreBreakdown || null
  );
  const [verdictsByIndex, setVerdictsByIndex] = useState(
    persistedState?.verdictsByIndex || {}
  );

  // ============ UI STATE (NOT PERSISTED) ============
  const [peekedAnswers, setPeekedAnswers] = useState({}); // { [questionIdx]: true } — inline answer checks
  const [showDocModal, setShowDocModal] = useState(false);
  // const [showKeyboardHints, setShowKeyboardHints] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [selectedQuizForDetail, setSelectedQuizForDetail] = useState(null);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const scrollBodyRef = useRef(null);

  // ============ COMPONENT LIFECYCLE ============

  // Track current question via IntersectionObserver
  const quizQuestions = quizResult?.questions ?? [];
  useEffect(() => {
    if (mode !== 'taking' || !quizQuestions.length) return;
    const root = scrollBodyRef.current;
    if (!root) return;
    const visibleSet = new Set();
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const idx = Number(entry.target.dataset.qidx);
          if (entry.isIntersecting) visibleSet.add(idx);
          else visibleSet.delete(idx);
        });
        if (visibleSet.size > 0) {
          setCurrentQuestionIdx(Math.min(...visibleSet));
        }
      },
      { root, rootMargin: '-20% 0px -40% 0px', threshold: 0 }
    );
    quizQuestions.forEach((_, i) => {
      const el = document.getElementById(`quiz-q-${i}`);
      if (!el) return;
      el.dataset.qidx = i;
      obs.observe(el);
    });
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, quizQuestions.length]);

  // Keyboard navigation for quiz taking
  useEffect(() => {
    if (mode !== 'taking' || !quizQuestions.length) return;
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        setCurrentQuestionIdx(prev => {
          const next = Math.min(prev + 1, quizQuestions.length - 1);
          const el = document.getElementById(`quiz-q-${next}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return next;
        });
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCurrentQuestionIdx(prev => {
          const next = Math.max(prev - 1, 0);
          const el = document.getElementById(`quiz-q-${next}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return next;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, quizQuestions.length]);

  // Track mounted state - DO NOT abort quiz generation on unmount
  // (allows quiz to complete even if StrictMode remounts or user switches tools)
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Keep onStateChangeRef in sync so generation handler can push state after unmount
  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  // On mount: consume any pending generation result that completed while unmounted
  useEffect(() => {
    if (pendingGenerationResult) {
      const result = pendingGenerationResult;
      pendingGenerationResult = null;
      console.log('[QuizzesTool] Consuming pending generation result on remount');
      setQuizResult(result.quizResult);
      setAttemptId(result.attemptId || null);
      setMode('taking');
      setCurrentIndex(0);
      setAnswersByIndex({});
      setSubmitted(false);
      setScoreBreakdown(null);
      setVerdictsByIndex({});
      setPeekedAnswers({});
      setLoading(false);
      setIsCreating(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle quiz selection from history panel - show detail view
  // Always switch to detail view, even if a quiz is in progress
  const handleSelectQuizFromPanel = useCallback((quiz) => {
    // Clear any active quiz state so the detail view can show
    if (quizResult) {
      setQuizResult(null);
      setAttemptId(null);
      setMode('idle');
      setCurrentIndex(0);
      setAnswersByIndex({});
      setSubmitted(false);
      setScoreBreakdown(null);
      setVerdictsByIndex({});
    }
    setSelectedQuizForDetail(quiz);
  }, [quizResult]);

  // ============ QUESTION NORMALIZATION ============
  const normalizeQuestion = useCallback((q) => {
    const rawType = q?.question_type || q?.type || q?.questionType || '';
    const question_type = String(rawType).toLowerCase().replace(/\s+/g, '_');

    const question_text = q?.question_text || q?.question || q?.prompt || q?.text || q?.questionText || '';

    // Extract choices from various API response shapes
    const rawChoices = 
      Array.isArray(q?.options) ? q.options :
      Array.isArray(q?.choices) ? q.choices :
      Array.isArray(q?.options?.choices) ? q.options.choices :
      Array.isArray(q?.answer_choices) ? q.answer_choices :
      Array.isArray(q?.answers) ? q.answers :
      [];
    
    const choices = rawChoices.map(o => (typeof o === 'string' ? o : (o?.text ?? o?.label ?? String(o))));

    // Normalize choices for true/false questions
    const normalizedChoices = question_type === 'true_false' ? ['True', 'False'] : choices;

    // Extract correct_index (number) or fall back to correct_answer (text)
    const correct_index = 
      typeof q?.correct_index === 'number' ? q.correct_index :
      typeof q?.options?.correct_index === 'number' ? q.options.correct_index :
      null;

    const correct_answer = q?.correct_answer ?? q?.answer ?? q?.correct ?? q?.correctAnswer ?? null;
    const explanation = q?.explanation ?? q?.rationale ?? '';
    const position = typeof q?.position === 'number' ? q.position : 0;

    return { 
      question_type, 
      question_text, 
      choices: normalizedChoices, 
      correct_index, 
      correct_answer, 
      explanation, 
      position,
      _raw: q 
    };
  }, []);

  const normalizeQuiz = useCallback((result, { requestedTypes, shuffle = true } = {}) => {
    let questions = Array.isArray(result?.questions) ? result.questions : [];
    let normalized = questions.map(normalizeQuestion);

    // Filter out question types the user didn't request (AI sometimes ignores the distribution)
    if (requestedTypes && requestedTypes.size > 0) {
      const typeMap = { mc: 'multiple_choice', tf: 'true_false', sa: 'short_answer' };
      const allowed = new Set([...requestedTypes].map(t => typeMap[t] || t));
      const filtered = normalized.filter(q => allowed.has(q.question_type));
      // Only apply filter if it doesn't remove ALL questions
      if (filtered.length > 0) normalized = filtered;
    }

    // Shuffle multiple-choice options so the correct answer isn't always A
    if (shuffle) {
    normalized = normalized.map(q => {
      if (q.question_type !== 'multiple_choice' || !q.choices || q.choices.length < 2) return q;

      // Determine original correct choice index
      let origCorrectIdx = typeof q.correct_index === 'number' ? q.correct_index : null;
      if (origCorrectIdx === null && q.correct_answer && q.choices.length) {
        const caLower = String(q.correct_answer).trim().toLowerCase();
        origCorrectIdx = q.choices.findIndex(c => String(c).trim().toLowerCase() === caLower);
        if (origCorrectIdx === -1) origCorrectIdx = null;
      }

      // Build index array and Fisher-Yates shuffle
      const indices = q.choices.map((_, i) => i);
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }

      const shuffledChoices = indices.map(i => q.choices[i]);
      const newCorrectIdx = origCorrectIdx !== null ? indices.indexOf(origCorrectIdx) : null;
      const newCorrectAnswer = newCorrectIdx !== null ? shuffledChoices[newCorrectIdx] : q.correct_answer;

      return {
        ...q,
        choices: shuffledChoices,
        correct_index: newCorrectIdx,
        correct_answer: newCorrectAnswer,
        _shuffled: true,
      };
    });
    } // end if (shuffle)

    // Shuffle question order so retakes feel fresh
    if (shuffle && normalized.length > 1) {
      for (let i = normalized.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [normalized[i], normalized[j]] = [normalized[j], normalized[i]];
      }
    }

    return {
      ...result,
      num_questions: result?.num_questions ?? normalized.length,
      questions: normalized,
    };
  }, [normalizeQuestion]);

  // Handle starting quiz from detail view
  const handleStartQuizFromDetail = useCallback(async (quiz, mode = 'start') => {
    try {
      addToast({ intent: 'info', message: 'Loading quiz...' });
      
      // Fetch full quiz with questions
      const quizSet = await getQuizSet(quiz.id);
      
      if (mode === 'review') {
        // TODO: Implement review mode from panel
        addToast({ intent: 'info', message: 'Review mode coming soon' });
        return;
      }
      
      // Start quiz - create new attempt
      const attemptResponse = await startQuizAttempt(quiz.id);
      
      // Normalize + shuffle choices for a fresh start
      const normalized = normalizeQuiz({ ...quizSet, quiz_set_id: quiz.id });
      setQuizResult(normalized);
      setAttemptId(attemptResponse.id);
      setMode('taking');
      setCurrentIndex(0);
      setAnswersByIndex({});
      setSubmitted(false);
      setScoreBreakdown(null);
      setVerdictsByIndex({});
      setPeekedAnswers({});
      setViewMode('builder'); // Switch to builder tab in collapsed mode
      setSelectedQuizForDetail(null); // Clear detail view
      
      addToast({ intent: 'success', message: 'Quiz started!' });
      
      // NOTE: Removed auto-expand behavior - quiz starts in current layout
    } catch (err) {
      console.error('Failed to load quiz:', err);
      addToast({ intent: 'danger', message: 'Failed to load quiz. Please try again.' });
    }
  }, [getQuizSet, startQuizAttempt, normalizeQuiz, addToast, setQuizResult, setAttemptId, setMode, setCurrentIndex, setAnswersByIndex, setSubmitted, setScoreBreakdown, setVerdictsByIndex]);

  // Handle clicking an attempt row in the detail view
  const handleSelectAttemptFromDetail = useCallback(async (quiz, attempt, action) => {
    try {
      if (action === 'continue') {
        addToast({ intent: 'info', message: 'Resuming quiz...' });
        
        // Fetch full quiz with questions
        const quizSet = await getQuizSet(quiz.id);
        
        // Resume the existing attempt (don't create new one)
        const normalized = normalizeQuiz({ ...quizSet, quiz_set_id: quiz.id });
        setQuizResult(normalized);
        setAttemptId(attempt.id);
        setMode('taking');
        setCurrentIndex(0);
        setAnswersByIndex({});
        setSubmitted(false);
        setScoreBreakdown(null);
        setVerdictsByIndex({});
        setPeekedAnswers({});
        setViewMode('builder');
        setSelectedQuizForDetail(null);
        
        addToast({ intent: 'success', message: 'Quiz resumed!' });
      } else if (action === 'review') {
        addToast({ intent: 'info', message: 'Loading results...' });
        
        // Fetch full quiz + attempt results
        const quizSet = await getQuizSet(quiz.id);
        const results = await getAttemptResults(attempt.id);
        
        // Build verdicts and answers from submissions
        // Verdict shape must match what renderSummary/renderReview expect:
        //   { verdict: 'correct'|'wrong'|'uncertain', confidence, gradingResult }
        const verdicts = {};
        const answers = {};
        const questions = quizSet.questions || [];
        let correct = 0;
        let wrong = 0;
        let uncertain = 0;
        
        if (results?.answerSubmissions) {
          results.answerSubmissions.forEach(sub => {
            const qIdx = questions.findIndex(q => q.id === sub.questionId);
            if (qIdx >= 0) {
              answers[qIdx] = sub.userAnswer;
              if (sub.isCorrect === true) {
                correct++;
                verdicts[qIdx] = { verdict: 'correct', confidence: 1.0, gradingResult: sub.gradingResult };
              } else if (sub.isCorrect === false) {
                wrong++;
                verdicts[qIdx] = { verdict: 'wrong', confidence: 1.0, gradingResult: sub.gradingResult };
              } else {
                uncertain++;
                verdicts[qIdx] = { verdict: 'uncertain', confidence: 0.5, gradingResult: sub.gradingResult };
              }
            }
          });
        }
        
        // Switch to summary (results) view — normalize but don't shuffle (reviewing past attempt)
        const normalizedForReview = normalizeQuiz({ ...quizSet, quiz_set_id: quiz.id }, { shuffle: false });
        setQuizResult(normalizedForReview);
        setAttemptId(attempt.id);
        setCurrentIndex(0);
        setAnswersByIndex(answers);
        setSubmitted(true);
        setScoreBreakdown({ correct, wrong, uncertain, total: questions.length });
        setVerdictsByIndex(verdicts);
        setViewMode('builder');
        setSelectedQuizForDetail(null);
        setMode('summary');
        
        addToast({ intent: 'success', message: 'Showing quiz results' });
      }
    } catch (err) {
      console.error('Failed to load attempt:', err);
      addToast({ intent: 'danger', message: 'Failed to load attempt. Please try again.' });
    }
  }, [getQuizSet, getAttemptResults, normalizeQuiz, addToast, setQuizResult, setAttemptId, setMode, setCurrentIndex, setAnswersByIndex, setSubmitted, setScoreBreakdown, setVerdictsByIndex]);

  // Sync incoming persistedState prop changes from parent
  useEffect(() => {
    if (!persistedState) return;

    // Handle external quiz selection from AIToolsPage sidebar FIRST.
    // Use a ref to track the last consumed selection so we don't re-process
    // the same quiz object on subsequent renders (which would loop).
    if (persistedState.externalSelectedQuiz &&
        persistedState.externalSelectedQuiz !== lastConsumedExternalQuizRef.current) {
      lastConsumedExternalQuizRef.current = persistedState.externalSelectedQuiz;
      // Clear any active quiz state so the detail view render condition (!quizResult) is met
      setQuizResult(null);
      setAttemptId(null);
      setMode('idle');
      setCurrentIndex(0);
      setAnswersByIndex({});
      setSubmitted(false);
      setScoreBreakdown(null);
      setVerdictsByIndex({});
      setSelectedQuizForDetail(persistedState.externalSelectedQuiz);
      return; // Don't run sync logic — we just overwrote local state
    }
    
    // Update state when parent pushes new quiz/attempt data via persistedState.
    // Use 'in' checks so explicit null values (e.g. quizResult: null from "New Quiz")
    // are propagated instead of being swallowed by a truthiness guard.
    if ('quizResult' in persistedState && persistedState.quizResult !== quizResult) {
      setQuizResult(persistedState.quizResult);
    }
    if (persistedState.attemptId !== undefined && persistedState.attemptId !== attemptId) {
      setAttemptId(persistedState.attemptId);
    }
    if (persistedState.mode && persistedState.mode !== mode) {
      setMode(persistedState.mode);
    }
    if (persistedState.viewMode && persistedState.viewMode !== viewMode) {
      setViewMode(persistedState.viewMode);
    }
    if (persistedState.currentIndex !== undefined && persistedState.currentIndex !== currentIndex) {
      setCurrentIndex(persistedState.currentIndex);
    }
    if ('answersByIndex' in persistedState && JSON.stringify(persistedState.answersByIndex) !== JSON.stringify(answersByIndex)) {
      setAnswersByIndex(persistedState.answersByIndex || {});
    }
    if (persistedState.submitted !== undefined && persistedState.submitted !== submitted) {
      setSubmitted(persistedState.submitted);
    }
    if ('scoreBreakdown' in persistedState && persistedState.scoreBreakdown !== scoreBreakdown) {
      setScoreBreakdown(persistedState.scoreBreakdown);
    }
    if ('verdictsByIndex' in persistedState && JSON.stringify(persistedState.verdictsByIndex) !== JSON.stringify(verdictsByIndex)) {
      setVerdictsByIndex(persistedState.verdictsByIndex || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistedState]);

  // Measure overlay height for proper list offset
  useEffect(() => {
    if (isCreating && overlayRef.current) {
      const height = overlayRef.current.offsetHeight;
      setOverlayHeight(height);
    } else {
      setOverlayHeight(0);
    }
  }, [isCreating]);

  // Auto-scroll question into view when navigating
  useEffect(() => {
    if (mode === 'taking' && cardTopRef.current) {
      cardTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentIndex, mode]);

  // Check state reset removed - feature not in use

  // ============ ABORT HELPER ============
  // Only abort when user explicitly cancels or starts new generation
  const abortActiveQuizRequest = useCallback((reason = 'replaced') => {
    const active = activeRequestRef.current;
    if (active?.controller && !active.controller.signal.aborted) {
      console.log('[QuizzesTool] Aborting request:', reason);
      active.controller.abort(reason);
    }
    activeRequestRef.current = null;
  }, []);

  // ============ RESPONSIVE LAYOUT ============
  // Track container width with ResizeObserver for responsive behavior
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Determine layout mode based on width
  // const isCompact = containerWidth < 520; // (not currently used)
  const isExpanded = containerWidth >= 980;

  // ============ QUIZ RESULT SAFETY ============
  // Clamp currentIndex when quizResult changes
  useEffect(() => {
    if (!quizResult) return;

    const total = quizResult?.questions?.length || 0;
    if (total > 0) {
      setCurrentIndex(i => Math.min(Math.max(i, 0), total - 1));
    }
  }, [quizResult]);

  // ============ STATE PERSISTENCE ============
  useEffect(() => {
    if (!onStateChange) return;

    const currentState = {
      selectedDocumentIds,
      numQuestions,
      difficultyLevel,
      questionTypePreset,
      quizResult,
      viewMode,
      attemptId,
      mode,
      currentIndex,
      answersByIndex,
      submitted,
      scoreBreakdown,
      verdictsByIndex,
      loading
    };

    const prevState = prevStateRef.current;
    if (!prevState || JSON.stringify(currentState) !== JSON.stringify(prevState)) {
      prevStateRef.current = currentState;
      onStateChange(currentState);
    }
  }, [
    selectedDocumentIds, numQuestions, difficultyLevel, questionTypePreset, quizResult, viewMode,
    attemptId, mode, currentIndex, answersByIndex, submitted, scoreBreakdown, verdictsByIndex,
    loading, onStateChange
  ]);

  // ============ DOCUMENT SELECTION ============
  const handleToggleDocument = useCallback((docId) => {
    setSelectedDocumentIds(prev => {
      const id = String(docId);
      if (prev.includes(id)) {
        return prev.filter(d => d !== id);
      } else {
        return [...prev, id];
      }
    });
  }, []);

  const handleConnectDocuments = useCallback((docIds) => {
    setSelectedDocumentIds(docIds.map(String));
    setShowDocModal(false);
  }, []);

  // ============ SHORT ANSWER GRADING ============
  const normalizeText = useCallback((text) => {
    return String(text || '')
      .toLowerCase()
      .trim()
      .replace(/[.,!?;:'"()[\]{}]/g, '') // Strip punctuation
      .replace(/\s+/g, ' '); // Collapse whitespace
  }, []);

  const calculateKeywordOverlap = useCallback((text1, text2) => {
    const words1 = new Set(normalizeText(text1).split(' ').filter(w => w.length > 0));
    const words2 = new Set(normalizeText(text2).split(' ').filter(w => w.length > 0));
    
    if (words1.size === 0 && words2.size === 0) return 1.0;
    if (words1.size === 0 || words2.size === 0) return 0.0;

    const intersection = new Set([...words1].filter(w => words2.has(w)));
    const union = new Set([...words1, ...words2]);
    
    return intersection.size / union.size; // Jaccard similarity
  }, [normalizeText]);

  const gradeAnswer = useCallback((qNorm, userAnswer) => {
    if (!userAnswer) {
      return { verdict: 'wrong', confidence: 1.0 };
    }

    const ua = String(userAnswer).trim();

    // Multiple choice: letter or text matching
    if (qNorm.question_type === 'multiple_choice') {
      const uaUpper = ua.toUpperCase();
      
      // If we have correct_index, convert user answer letter to index and compare
      if (typeof qNorm.correct_index === 'number' && /^[A-Z]$/i.test(ua)) {
        const userIdx = uaUpper.charCodeAt(0) - 65;
        return {
          verdict: userIdx === qNorm.correct_index ? 'correct' : 'wrong',
          confidence: 1.0
        };
      }
      
      // Fall back to correct_answer text matching
      if (qNorm.correct_answer) {
        const ca = String(qNorm.correct_answer).trim();
        const caUpper = ca.toUpperCase();

        // Letter match (A/B/C/D)
        if (/^[A-D]$/i.test(ca)) {
          return {
            verdict: caUpper === uaUpper ? 'correct' : 'wrong',
            confidence: 1.0
          };
        }

        // Text match with choice lookup
        if (qNorm.choices?.length) {
          const chosenIdx = uaUpper.charCodeAt(0) - 65;
          const chosenText = qNorm.choices[chosenIdx];
          if (chosenText && normalizeText(ca) === normalizeText(chosenText)) {
            return { verdict: 'correct', confidence: 1.0 };
          }
        }

        // Direct text match
        return {
          verdict: normalizeText(ca) === normalizeText(ua) ? 'correct' : 'wrong',
          confidence: 1.0
        };
      }
      
      return { verdict: 'wrong', confidence: 0.5 };
    }

    // True/False
    if (qNorm.question_type === 'true_false') {
      if (!qNorm.correct_answer) {
        return { verdict: 'wrong', confidence: 1.0 };
      }
      const ca = String(qNorm.correct_answer).trim();
      // Resolve letter answers (A/B) to text (True/False) for comparison
      let resolvedUa = ua;
      if (/^[AB]$/i.test(ua)) {
        const tfChoices = ['True', 'False'];
        const idx = ua.toUpperCase().charCodeAt(0) - 65;
        resolvedUa = tfChoices[idx] || ua;
      }
      return {
        verdict: normalizeText(ca) === normalizeText(resolvedUa) ? 'correct' : 'wrong',
        confidence: 1.0
      };
    }

    // Short answer: confidence-based grading
    if (qNorm.question_type === 'short_answer') {
      if (!qNorm.correct_answer) {
        return { verdict: 'wrong', confidence: 1.0 };
      }
      const ca = String(qNorm.correct_answer).trim();
      const norm_ca = normalizeText(ca);
      const norm_ua = normalizeText(ua);

      // Exact match
      if (norm_ca === norm_ua) {
        return { verdict: 'correct', confidence: 1.0 };
      }

      // Keyword overlap
      const overlap = calculateKeywordOverlap(ca, ua);
      
      if (overlap >= 0.85) {
        return { verdict: 'correct', confidence: overlap };
      } else if (overlap <= 0.40) {
        return { verdict: 'wrong', confidence: 1.0 - overlap };
      } else {
        return { verdict: 'uncertain', confidence: overlap };
      }
    }

    return { verdict: 'wrong', confidence: 0.5 };
  }, [normalizeText, calculateKeywordOverlap]);

  // ============ QUESTION TYPE PRESETS ============
  const getQuestionTypeDistribution = useCallback((preset) => {
    // Parse comma-separated type keys: mc, tf, sa
    const types = new Set(preset.split(','));
    const count = types.size || 1;
    const perType = Math.floor(numQuestions / count);
    const remainder = numQuestions - perType * count;
    const dist = {};
    let i = 0;
    if (types.has('mc')) { dist.multiple_choice = perType + (i < remainder ? 1 : 0); i++; }
    if (types.has('tf')) { dist.true_false = perType + (i < remainder ? 1 : 0); i++; }
    if (types.has('sa')) { dist.short_answer = perType + (i < remainder ? 1 : 0); i++; }
    // Fallback if preset was unrecognised
    if (Object.keys(dist).length === 0) {
      dist.multiple_choice = Math.floor(numQuestions * 0.6);
      dist.true_false = Math.floor(numQuestions * 0.2);
      dist.short_answer = Math.floor(numQuestions * 0.2);
    }
    return dist;
  }, [numQuestions]);

  // ============ QUIZ GENERATION ============
  const handleGenerateQuiz = useCallback(async () => {
    // Premium gate: block new creation for non-entitled users
    if (isCreationBlocked) {
      setShowUpgradeModal(true);
      return;
    }
    if (selectedDocumentIds.length === 0) return;
    if (isGeneratingRef.current) return; // Hard lock to prevent double-click
    
    isGeneratingRef.current = true;
    setLoading(true);
    setError(null);

    // Track quiz generation start
    const corrId = UsageEventsTracker.startCorrelation('quiz');
    UsageEventsTracker.recordUserAction('QUIZ_GENERATION_STARTED', {
      action: 'generate',
      category: 'ai_tool',
      label: 'quiz_start',
      metadata: {
        documentCount: selectedDocumentIds.length,
        numQuestions,
        difficultyLevel,
        courseId: selectedCourse?.id || selectedCourse?.endeavorId
      }
    }, { correlationId: corrId });

    // Abort any previous quiz generation (user clicked Generate again)
    abortActiveQuizRequest('new-generate');

    // Create new controller with unique requestId
    const controller = new AbortController();
    const requestId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    activeRequestRef.current = { controller, requestId };

    let genJobId = null;
    try {
      const userId = user?.id || user?.userId || user?.user_id;
      const endeavorId = selectedCourse?.id || selectedCourse?.endeavorId || null;

      // Build a meaningful title from source document names
      let quizTitle = `${new Date().toLocaleDateString()}`;
      try {
        if (selectedDocumentIds.length > 0 && getDocumentById) {
          const firstDoc = await getDocumentById(selectedDocumentIds[0]);
          const firstName = firstDoc?.name || firstDoc?.title || firstDoc?.fileName;
          if (firstName) {
            const truncName = firstName.length > 50 ? firstName.substring(0, 47) + '...' : firstName;
            if (selectedDocumentIds.length === 1) {
              quizTitle = `${truncName} - ${new Date().toLocaleDateString()}`;
            } else {
              quizTitle = `${truncName} + ${selectedDocumentIds.length - 1} more - ${new Date().toLocaleDateString()}`;
            }
          }
        }
      } catch (docErr) {
        console.warn('[QuizzesTool] Could not resolve document names for title:', docErr);
      }

      const payload = {
        document_ids: selectedDocumentIds.map(Number),
        num_questions: numQuestions,
        difficulty_level: difficultyLevel,
        question_type_distribution: getQuestionTypeDistribution(questionTypePreset),
        user_id: userId,
        endeavor_id: endeavorId,
        title: quizTitle,
        description: `Generated from ${selectedDocumentIds.length} document(s)`
      };

      console.log('[QuizzesTool] requestId start:', requestId);
      console.log('[QuizzesTool] Generating quiz with payload:', payload);

      // Register processing job for sidebar phantom card
      const genJob = registerJob({
        toolType: 'quiz',
        title: quizTitle,
        metadata: {
          documentCount: selectedDocumentIds.length,
          numQuestions,
          difficultyLevel,
        },
      });
      genJobId = genJob.id;
      
      // Pass abort signal to API
      const result = await AiStudyGenerationApi.generateQuiz(payload, { signal: controller.signal });
      
      // Check if this request was superseded by a newer one
      if (activeRequestRef.current?.requestId !== requestId) {
        console.log('[QuizzesTool] Request superseded, ignoring result');
        return;
      }
      
      console.log('[QuizzesTool] Quiz generation parsed result:', result);
      
      // Normalize response - handle both { quiz: {...} } and direct { questions: [...] } shapes
      const rawQuiz = result?.quiz ?? result;
      const requestedTypes = new Set(questionTypePreset.split(','));
      const normalized = normalizeQuiz(rawQuiz, { requestedTypes });
      
      // Validate quiz payload
      const questions = normalized?.questions;
      if (!Array.isArray(questions) || questions.length === 0) {
        console.warn('[QuizzesTool] No questions returned:', normalized);
        setError('Quiz generated but returned 0 questions. Try again or select different documents.');
        if (genJobId) failJob(genJobId, 'Quiz returned 0 questions');
        
        // Track quiz generation error
        UsageEventsTracker.recordUserAction('QUIZ_GENERATION_FAILED', {
          action: 'error',
          category: 'ai_tool',
          label: 'quiz_no_questions',
          metadata: {
            documentCount: selectedDocumentIds.length
          }
        }, { correlationId: UsageEventsTracker.getCorrelation('quiz') });
        UsageEventsTracker.clearCorrelation('quiz');
        return;
      }
      
      // Warn if some questions have no choices (but still allow the quiz)
      if (normalized.questions.some(q => (q.question_type !== 'short_answer') && (q.choices?.length ?? 0) === 0)) {
        console.warn('[QuizzesTool] Some questions have no choices after normalization', normalized);
      }
      
      // Notify sidebar history panel to refresh (clear stale cache first)
      if (dataContext?.clearCache) dataContext.clearCache('quiz-sets');
      window.dispatchEvent(new CustomEvent('quizzes:created', { detail: { quiz: normalized } }));
      if (genJobId) completeJob(genJobId);
      UsageEventsTracker.recordUserAction('QUIZ_GENERATION_SUCCESS', {
        action: 'complete',
        category: 'ai_tool',
        entityType: 'quiz',
        entityId: normalized.quiz_set_id,
        label: 'quiz_generated',
        value: normalized.questions?.length || 0,
        metadata: {
          quizId: normalized.quiz_set_id,
          questionCount: normalized.questions?.length || 0,
          documentCount: selectedDocumentIds.length,
          difficultyLevel
        }
      }, { correlationId: UsageEventsTracker.getCorrelation('quiz') });
      UsageEventsTracker.clearCorrelation('quiz');
      
      // Create quiz attempt
      let resolvedAttemptId = null;
      try {
        if (normalized.quiz_set_id) {
          const attemptResponse = await startQuizAttempt(normalized.quiz_set_id);
          // Parse response - startQuizAttempt returns a raw Response object
          const attemptData = attemptResponse?.json ? await attemptResponse.json() : attemptResponse;
          if (attemptData?.id) {
            resolvedAttemptId = attemptData.id;
          }
        }
      } catch (attemptErr) {
        console.error('[QuizzesTool] Failed to create attempt:', attemptErr);
      }

      // If component is still mounted, apply state normally
      if (isMountedRef.current) {
        setQuizResult(normalized);
        if (resolvedAttemptId) setAttemptId(resolvedAttemptId);
        setMode('taking');
        setCurrentIndex(0);
        setAnswersByIndex({});
        setSubmitted(false);
        setScoreBreakdown(null);
        setVerdictsByIndex({});
        setPeekedAnswers({});
        setIsCreating(false);
        addToast({ intent: 'success', message: '📝 Quiz generated successfully!' });
      } else {
        // Component unmounted during generation — stash result for remount
        console.log('[QuizzesTool] Generation completed while unmounted, stashing result');
        pendingGenerationResult = {
          quizResult: normalized,
          attemptId: resolvedAttemptId,
        };
        // Also push state to parent so it persists across tab switches
        if (onStateChangeRef.current) {
          onStateChangeRef.current({
            quizResult: normalized,
            attemptId: resolvedAttemptId,
            mode: 'taking',
            currentIndex: 0,
            answersByIndex: {},
            submitted: false,
            scoreBreakdown: null,
            verdictsByIndex: {},
            loading: false,
          });
        }
      }
    } catch (err) {
      // If aborted, exit gracefully (no error UI)
      if (err?.name === 'AbortError' || String(err?.message || '').includes('aborted')) {
        console.warn('[QuizzesTool] Request aborted:', err?.message || 'AbortError');
        return;
      }

      console.error('[QuizzesTool] Quiz generation error:', err);
      
      // Check if this request was superseded
      if (activeRequestRef.current?.requestId !== requestId) return;
      
      // Better error messages based on error type
      let errorMessage = 'Failed to generate quiz. Please try again.';
      if (err.message?.includes('404') || err.status === 404) {
        errorMessage = 'Quiz generation endpoint not available. The AI service may not have this feature deployed yet.';
      } else if (err.message?.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      if (genJobId) failJob(genJobId, errorMessage);
    } finally {
      // Only clear refs if still current request
      if (activeRequestRef.current?.requestId === requestId) {
        isGeneratingRef.current = false;
        activeRequestRef.current = null;
        // Don't call setLoading(false) here — the MiniGameLoadingOverlay
        // controls its own dismissal via onReady/onExit callbacks.
        // Calling it here would unmount the overlay and kill any active game.
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocumentIds, numQuestions, difficultyLevel, questionTypePreset, getQuestionTypeDistribution, user, selectedCourse, abortActiveQuizRequest, normalizeQuiz, addToast, startQuizAttempt, dataContext, isCreationBlocked]);

  const handleNewQuiz = useCallback(() => {
    if (isCreationBlocked) {
      setShowUpgradeModal(true);
      return;
    }
    // Abort any in-flight generation so the user can start fresh
    abortActiveQuizRequest('new-quiz');
    isGeneratingRef.current = false;
    activeRequestRef.current = null;
    pendingGenerationResult = null;

    setQuizResult(null);
    setAttemptId(null);
    setError(null);
    setLoading(false);
    setMode('setup');
    setCurrentIndex(0);
    setAnswersByIndex({});
    setSubmitted(false);
    setScoreBreakdown(null);
    setVerdictsByIndex({});
    setSelectedQuizForDetail(null);
    setPeekedAnswers({});
  }, [abortActiveQuizRequest, isCreationBlocked]);

  // ============ QUIZ COMPLETION ============
  const handleFinishQuiz = useCallback(async () => {
    const questions = quizResult?.questions ?? [];
    const total = questions.length;
    
    // If no attemptId, create one first
    let currentAttemptId = attemptId;
    if (!currentAttemptId && quizResult?.quiz_set_id) {
      try {
        const attemptResponse = await startQuizAttempt(quizResult.quiz_set_id);
        // Parse response - startQuizAttempt returns a raw Response object
        const attemptData = attemptResponse?.json ? await attemptResponse.json() : attemptResponse;
        currentAttemptId = attemptData?.id;
        setAttemptId(currentAttemptId);
      } catch (err) {
        console.error('[QuizzesTool] Failed to create attempt on submit:', err);
        addToast({ intent: 'danger', message: 'Failed to submit quiz. Please try again.' });
        return;
      }
    }
    
    // If still no attemptId, fall back to local grading
    if (!currentAttemptId) {
      console.warn('[QuizzesTool] No attemptId available, using local grading');
      localGradeQuiz();
      return;
    }
    
    setLoading(true);
    addToast({ intent: 'info', message: 'Submitting quiz...' });
    
    try {
      // Build submissions array — resolve letter answers to choice text for backend
      const submissions = questions.map((q, i) => {
        const questionId = q?.id || q?._raw?.id;
        const raw = answersByIndex[i] || '';
        const qNorm = normalizeQuestion(q);
        let userAnswer = raw;
        // For MC, backend expects the choice text, not the letter
        if (qNorm.question_type === 'multiple_choice' && /^[A-Z]$/i.test(raw) && qNorm.choices?.length) {
          const choiceIdx = raw.toUpperCase().charCodeAt(0) - 65;
          if (choiceIdx >= 0 && choiceIdx < qNorm.choices.length) {
            userAnswer = qNorm.choices[choiceIdx];
          }
        }
        return { questionId, userAnswer };
      }).filter(s => s.questionId); // Only include questions with IDs
      
      // Submit answers to backend
      const submitResponse = await submitQuizAnswers(currentAttemptId, { answers: submissions });
      
      // Parse response (submitQuizAnswers returns a raw Response object)
      const submitData = submitResponse?.json ? await submitResponse.json() : submitResponse;
      
      // Check if AI grading is needed: look for ungraded submissions (isCorrect === null)
      const shortAnswerSubmissions = submitData?.answerSubmissions?.filter(s => s.isCorrect === null) || [];
      
      if (shortAnswerSubmissions.length > 0) {
        // Show grading spinner (separate from generation loading)
        setLoading(false);
        setIsGrading(true);
        addToast({ intent: 'info', message: 'Checking answers with AI...' });
        
        try {
          await AiStudyGenerationApi.gradeQuizAttempt(currentAttemptId);
          addToast({ intent: 'success', message: 'Quiz graded!' });
        } catch (gradingErr) {
          console.error('[QuizzesTool] AI grading failed:', gradingErr);
          addToast({ intent: 'warning', message: 'AI grading unavailable. Showing results without short-answer scores.' });
        } finally {
          setIsGrading(false);
        }
      }
      
      // Fetch final results (includes AI-graded scores if available)
      const resultsResponse = await getAttemptResults(currentAttemptId);
      const resultsData = resultsResponse?.json ? await resultsResponse.json() : resultsResponse;
      
      // Invalidate cache for this quiz set
      if (quizResult?.quiz_set_id) {
        invalidateQuizAttemptCaches(quizResult.quiz_set_id);
      }
      
      // Map results to UI state
      const answerSubmissions = resultsData?.answerSubmissions || resultsResponse?.answerSubmissions || [];
      let correct = 0;
      let wrong = 0;
      let uncertain = 0;
      const newVerdicts = {};
      
      questions.forEach((q, i) => {
        const questionId = q?.id || q?._raw?.id;
        const submission = answerSubmissions.find(s => s.questionId === questionId);
        
        if (submission) {
          const isCorrect = submission.isCorrect === true;
          const isWrong = submission.isCorrect === false;
          const isUncertain = submission.isCorrect === null;
          
          if (isCorrect) {
            correct++;
            newVerdicts[i] = { verdict: 'correct', confidence: 1.0, gradingResult: submission.gradingResult };
          } else if (isWrong) {
            wrong++;
            newVerdicts[i] = { verdict: 'wrong', confidence: 1.0, gradingResult: submission.gradingResult };
          } else if (isUncertain) {
            uncertain++;
            newVerdicts[i] = { verdict: 'uncertain', confidence: 0.5, gradingResult: submission.gradingResult };
          }
        } else {
          // Fallback to local grading if submission not found
          const qNorm = normalizeQuestion(q);
          const ua = answersByIndex[i];
          const result = gradeAnswer(qNorm, ua);
          newVerdicts[i] = result;
          if (result.verdict === 'correct') correct++;
          else if (result.verdict === 'wrong') wrong++;
          else if (result.verdict === 'uncertain') uncertain++;
        }
      });
      
      setScoreBreakdown({ correct, wrong, uncertain, total });
      setVerdictsByIndex(newVerdicts);
      setSubmitted(true);
      setMode('summary');
      
    } catch (err) {
      console.error('[QuizzesTool] Quiz submission error:', err);
      addToast({ intent: 'danger', message: 'Failed to submit quiz. Please try again.' });
    } finally {
      setLoading(false);
    }
    
    // Local grading fallback function
    function localGradeQuiz() {
      let correct = 0;
      let wrong = 0;
      let uncertain = 0;
      const newVerdicts = {};

      for (let i = 0; i < total; i++) {
        const qNorm = normalizeQuestion(questions[i]);
        const ua = answersByIndex[i];
        const result = gradeAnswer(qNorm, ua);
        
        newVerdicts[i] = result;

        if (result.verdict === 'correct') correct++;
        else if (result.verdict === 'wrong') wrong++;
        else if (result.verdict === 'uncertain') uncertain++;
      }

      setScoreBreakdown({ correct, wrong, uncertain, total });
      setVerdictsByIndex(newVerdicts);
      setSubmitted(true);
      setMode('summary');
    }
  }, [quizResult, answersByIndex, attemptId, normalizeQuestion, gradeAnswer, startQuizAttempt, submitQuizAnswers, getAttemptResults, invalidateQuizAttemptCaches, addToast]);

  // ============ KEYBOARD NAVIGATION ============
  useEffect(() => {
    if (mode !== 'taking' || showDocModal) return;

    const handleKeyDown = (e) => {
      const target = e.target;
      const isTextarea = target.tagName === 'TEXTAREA';
      const isInput = target.tagName === 'INPUT';

      // Allow Ctrl/Cmd+Enter in textarea
      if (isTextarea && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const questions = quizResult?.questions ?? [];
        const isLast = currentIndex === questions.length - 1;
        const selected = answersByIndex[currentIndex];
        const canProceed = selected && String(selected).trim().length > 0;
        
        if (canProceed) {
          if (isLast) {
            handleFinishQuiz();
          } else {
            setCurrentIndex(i => i + 1);
          }
        }
        return;
      }

      // Ignore other keys when in inputs/textarea
      if (isInput || isTextarea) return;

      const questions = quizResult?.questions ?? [];
      if (!questions.length) return;

      const q = normalizeQuestion(questions[currentIndex]);
      const selected = answersByIndex[currentIndex];
      const canProceed = q.question_type === 'short_answer'
        ? (selected && String(selected).trim().length > 0)
        : !!selected;
      const isLast = currentIndex === questions.length - 1;

      // Enter: Next/Finish
      if (e.key === 'Enter' && canProceed) {
        e.preventDefault();
        if (isLast) {
          handleFinishQuiz();
        } else {
          setCurrentIndex(i => i + 1);
        }
        return;
      }

      // ArrowLeft: Back
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        e.preventDefault();
        setCurrentIndex(i => i - 1);
        return;
      }

      // ArrowRight: Next
      if (e.key === 'ArrowRight' && canProceed && !isLast) {
        e.preventDefault();
        setCurrentIndex(i => i + 1);
        return;
      }

      // MCQ: 1-4 or A-D
      if (q.question_type === 'multiple_choice' && q.choices?.length) {
        const numMatch = e.key.match(/^[1-9]$/);
        const letterMatch = e.key.toUpperCase().match(/^[A-Z]$/);
        
        if (numMatch) {
          const idx = parseInt(e.key) - 1;
          if (idx < q.choices.length) {
            e.preventDefault();
            const letter = String.fromCharCode(65 + idx);
            setAnswersByIndex(prev => ({ ...prev, [currentIndex]: letter }));
          }
        } else if (letterMatch) {
          const idx = e.key.toUpperCase().charCodeAt(0) - 65;
          if (idx < q.choices.length) {
            e.preventDefault();
            setAnswersByIndex(prev => ({ ...prev, [currentIndex]: e.key.toUpperCase() }));
          }
        }
        return;
      }

      // True/False: T/F
      if (q.question_type === 'true_false') {
        if (e.key.toUpperCase() === 'T') {
          e.preventDefault();
          setAnswersByIndex(prev => ({ ...prev, [currentIndex]: 'True' }));
        } else if (e.key.toUpperCase() === 'F') {
          e.preventDefault();
          setAnswersByIndex(prev => ({ ...prev, [currentIndex]: 'False' }));
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mode, showDocModal, quizResult, currentIndex, answersByIndex, normalizeQuestion, handleFinishQuiz]);

  // ============ RENDER HELPERS ============
  // renderStepper removed (unused)

  const renderChoices = useCallback(({ question, selectedIndex, onSelect, isExpanded }) => {
    const hasChoices = (question?.choices?.length ?? 0) > 0;
    const isChoiceType = question?.question_type === 'multiple_choice' || question?.question_type === 'true_false';

    if (!isChoiceType) {
      return (
        <div className="short-answer">
          <textarea
            className="quiz-textarea"
            value={selectedIndex || ''}
            onChange={(e) => onSelect(e.target.value)}
            placeholder="Type your answer... (Ctrl+Enter to submit)"
            rows={4}
          />
        </div>
      );
    }

    if (!hasChoices) {
      return (
        <div className="quiz-empty-state" style={{ marginTop: '10px' }}>
          No answer choices were generated for this question. Try regenerating the quiz.
        </div>
      );
    }

    const isTF = question?.question_type === 'true_false';

    // Expanded view: use select dot pattern
    if (isExpanded) {
      return (
        <div className="quizChoices">
          {question.choices.map((choice, idx) => {
            const letter = String.fromCharCode(65 + idx);
            // T/F stores text ("True"/"False"); MC stores letter ("A"/"B"/...)
            const selectValue = isTF ? choice : letter;
            const isSelected = isTF
              ? selectedIndex === choice
              : (selectedIndex === letter || selectedIndex === idx);

            return (
              <div
                key={idx}
                className={`quizChoiceRow ${isSelected ? 'isSelected' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(selectValue)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(selectValue);
                  }
                }}
              >
                <button
                  type="button"
                  className={`quizSelectDot ${isSelected ? 'isSelected' : ''}`}
                  aria-label={`Select option ${letter}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(selectValue);
                  }}
                >
                  {letter}
                </button>

                <div className="quizChoiceContent">
                  <div className="quizChoiceText">{choice}</div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Compact view: use same letter button pattern as expanded
    return (
      <div className="quizChoices">
        {question.choices.map((choice, idx) => {
          const letter = String.fromCharCode(65 + idx);
          const selectValue = isTF ? choice : letter;
          const isSelected = isTF
            ? selectedIndex === choice
            : (selectedIndex === letter || selectedIndex === idx);

          return (
            <div
              key={idx}
              className={`quizChoiceRow ${isSelected ? 'isSelected' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(selectValue)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(selectValue);
                }
              }}
            >
              <button
                type="button"
                className={`quizSelectDot ${isSelected ? 'isSelected' : ''}`}
                aria-label={`Select option ${letter}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(selectValue);
                }}
              >
                {letter}
              </button>

              <div className="quizChoiceContent">
                <div className="quizChoiceText">{choice}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, []);

  const renderGeneratorForm = () => {
    if (loading) {
      return renderLoadingState();
    }

    if (error && !quizResult) {
      return renderErrorState();
    }

    return (
      <div className="quiz-generator">
        {!loading && !error && (
          <div className="quiz-settings">
            <div className="control-group">
              <label className="control-label">Documents</label>
              <button 
                className="ui-btn ui-btn--neutral"
                onClick={() => { if (isCreationBlocked) { setShowUpgradeModal(true); return; } setShowDocModal(true); }}
              >
                📄 {selectedDocumentIds.length === 0 ? 'Select Documents' : `${selectedDocumentIds.length} Selected`}
              </button>
            </div>

            <div className="control-group">
              <label className="control-label" htmlFor="num-questions">
                Questions: {numQuestions}
              </label>
              <input
                id="num-questions"
                type="range"
                min="5"
                max="50"
                step="5"
                value={numQuestions}
                onChange={(e) => setNumQuestions(Number(e.target.value))}
                className="quiz-slider"
              />
            </div>

            <div className="control-group">
              <label className="control-label">Difficulty</label>
              <div className="button-group">
                {['beginner', 'intermediate', 'advanced'].map(level => (
                  <button
                    key={level}
                    className={`ui-btn ui-btn--sm ${difficultyLevel === level ? 'ui-btn--primary' : 'ui-btn--neutral'}`}
                    onClick={() => setDifficultyLevel(level)}
                  >
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="control-group">
              <label className="control-label">Question Types</label>
              <div className="button-group">
                {[
                  { value: 'mixed', label: 'Mixed' },
                  { value: 'multiple_choice_only', label: 'Multiple Choice' },
                  { value: 'short_answer_only', label: 'Short Answer' }
                ].map(type => (
                  <button
                    key={type.value}
                    className={`ui-btn ui-btn--sm ${questionTypePreset === type.value ? 'ui-btn--primary' : 'ui-btn--neutral'}`}
                    onClick={() => setQuestionTypePreset(type.value)}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {!loading && !error && (
          <button
            className="ui-btn ui-btn--primary quiz-generate-btn"
            onClick={handleGenerateQuiz}
            disabled={selectedDocumentIds.length === 0 || loading}
          >
            {loading ? 'Generating...' : '✨ Generate Quiz'}
          </button>
        )}
      </div>
    );
  };

  // renderControls removed - now using renderGeneratorForm
  void renderGeneratorForm;

  const renderLoadingState = (isCompact = false) => {
    const loadStatus = error ? 'error' : (quizResult ? 'ready' : 'loading');
    return (
      <MiniGameLoadingOverlay
        loadStatus={loadStatus}
        contentType="quizzes"
        onReady={() => {
          setLoading(false);
          setMode('taking');
        }}
        onRetry={handleGenerateQuiz}
        onExit={() => {
          abortActiveQuizRequest('user-cancel');
          setLoading(false);
          setError(null);
        }}
        canAbort={true}
        errorMessage={error || "Couldn't load quiz. Check connection."}
        isCompact={isCompact}
      />
    );
  };

  const renderErrorState = () => (
    <div className="quiz-error">
      <div className="error-icon">⚠️</div>
      <h3>Generation Failed</h3>
      <p>{error}</p>
      <button 
        className="ui-btn ui-btn--primary"
        onClick={handleGenerateQuiz}
      >
        Try Again
      </button>
    </div>
  );

  const renderTaking = () => {
    const questions = quizResult?.questions ?? [];
    if (!questions.length) return null;

    console.log('QUIZ DEBUG - Full quizResult:', quizResult);
    console.log('QUIZ DEBUG - First question:', questions[0]);
    console.log('QUIZ DEBUG - Normalized first question:', normalizeQuestion(questions[0]));

    const total = questions.length;
    const answeredCount = Object.keys(answersByIndex).length;
    const allAnswered = answeredCount === total;

    const handleSelectAnswer = (questionIndex, value) => {
      setAnswersByIndex(prev => ({ ...prev, [questionIndex]: value }));
    };

    const scrollToQuestion = (idx) => {
      const el = document.getElementById(`quiz-q-${idx}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Also scroll the navigator to keep the clicked button visible
      const navBtn = document.querySelector(`.quizNavigator button:nth-child(${idx + 1})`);
      if (navBtn) navBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    };

    return (
      <div className="quizTakingContainer">
        {/* Header – sticky top */}
        <div className="quizTakingHeader">
          <h2 className="quizTakingTitle">Quiz</h2>
          <button type="button" className="ui-btn ui-btn--neutral" onClick={handleNewQuiz}>
            Exit Quiz
          </button>
        </div>

        {/* Sticky Question Navigator */}
        <div className="quizNavigator">
          {questions.map((_, idx) => {
            const sel = answersByIndex[idx];
            const ans = sel !== undefined && sel !== null && String(sel).trim() !== '';
            const isCurrent = idx === currentQuestionIdx;
            return (
              <button
                key={idx}
                type="button"
                className={`quizNavBtn${ans ? ' quizNavBtn--answered' : ''}${isCurrent ? ' quizNavBtn--current' : ''}`}
                onClick={() => scrollToQuestion(idx)}
                title={`Question ${idx + 1}${ans ? ' (answered)' : ''}${isCurrent ? ' (current)' : ''}`}
              >
                {idx + 1}
              </button>
            );
          })}
        </div>

        {/* Scrollable questions */}
        <div className="quizTakingScrollBody" ref={scrollBodyRef}>
          <div className="quizQuestionsArea">
            {questions.map((rawQ, idx) => {
              const q = normalizeQuestion(rawQ);
              const selected = answersByIndex[idx];
              const isAnswered = selected !== undefined && selected !== null && String(selected).trim() !== '';

              return (
                <div key={idx} id={`quiz-q-${idx}`} className={`quizQuestionCard ${isAnswered ? 'isAnswered' : ''}`}>
                  <div className="quizQuestionCardHeader">
                    <div className="quizQuestionNumber">Question {idx + 1}</div>
                    <div className="quizQuestionType">
                      {q.question_type === 'true_false' ? 'True / False' : 
                       q.question_type === 'multiple_choice' ? 'Multiple Choice' : 'Short Answer'}
                    </div>
                  </div>

                  <div className="quizQuestionText">{q.question_text}</div>

                  <div className="quizQuestionAnswers">
                    {renderChoices({
                      question: q,
                      selectedIndex: selected,
                      onSelect: (value) => handleSelectAnswer(idx, value),
                      isExpanded: true,
                    })}
                  </div>

                  {/* Answered badge + Check Answer button */}
                  <div className="quizQuestionFooter">
                    {isAnswered && (
                      <div className="quizQuestionAnsweredBadge">✓ Answered</div>
                    )}
                    {isAnswered && !peekedAnswers[idx] && (
                      <button
                        type="button"
                        className="quizCheckAnswerBtn"
                        onClick={() => setPeekedAnswers(prev => ({ ...prev, [idx]: true }))}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        Check Answer
                      </button>
                    )}
                  </div>

                  {/* Inline answer reveal */}
                  {peekedAnswers[idx] && (() => {
                    const peekVerdict = gradeAnswer(q, selected);
                    const isRight = peekVerdict.verdict === 'correct';
                    // Resolve user's display answer
                    let userDisplay = selected || '—'; // eslint-disable-line no-unused-vars
                    if (selected && q.choices?.length && /^[A-Z]$/.test(selected)) {
                      const ci = selected.charCodeAt(0) - 65;
                      if (ci >= 0 && ci < q.choices.length) userDisplay = q.choices[ci]; // eslint-disable-line no-unused-vars
                    }
                    return (
                      <div className={`quizPeekReveal ${isRight ? 'quizPeekReveal--correct' : 'quizPeekReveal--wrong'}`}>
                        <div className="quizPeekVerdict">
                          {isRight
                            ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Correct!</>
                            : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Incorrect</>
                          }
                        </div>
                        {!isRight && (
                          <div className="quizPeekCorrect">
                            <span className="quizPeekLabel">Correct answer:</span> {q.correct_answer ?? '—'}
                          </div>
                        )}
                        {q.explanation && (
                          <div className="quizPeekExplanation">{q.explanation}</div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </div>

        {/* Compact submit bar – sticky bottom */}
        <div className="quizSubmitArea">
          <div className="quizProgressBar">
            <div className="quizProgressBarFill" style={{ width: `${total > 0 ? (answeredCount / total) * 100 : 0}%` }} />
          </div>
          <div className="quizSubmitRow">
            <span className={`quizSubmitStatus${allAnswered ? ' quizSubmitStatus--ready' : ''}`}>
              {allAnswered ? '✓ All questions answered' : `${answeredCount} / ${total} answered`}
            </span>
            <button
              type="button"
              className="ui-btn ui-btn--primary quizSubmitButton"
              onClick={handleFinishQuiz}
              disabled={!allAnswered}
            >
              Submit Quiz
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    if (!scoreBreakdown) return null;

    const { correct, wrong, uncertain } = scoreBreakdown;
    const graded = correct + wrong;
    const pct = graded > 0 ? Math.round((correct / graded) * 100) : 0;
    const accuracy = graded > 0 ? pct : 0;
    
    // Get Scout's reaction based on score
    const scoutState = getScoutSummaryState(pct);

    return (
      <div className={`quizResultsWrap ${isExpanded ? 'isExpanded' : 'isCompact'}`}>
        <div className="quizResultsCard">
          {/* 1. Title */}
          <div className="quizResultsHeader">
            <h3>Quiz Complete!</h3>
          </div>

          {/* 2. Score hero with progress ring */}
          <div className="scoreHero">
            <div className="scoreRing">
              <svg className="scoreRingSvg" viewBox="0 0 120 120">
                <circle className="scoreRingTrack" cx="60" cy="60" r="52" />
                <circle
                  className="scoreRingFill"
                  cx="60" cy="60" r="52"
                  strokeDasharray={2 * Math.PI * 52}
                  strokeDashoffset={2 * Math.PI * 52 * (1 - pct / 100)}
                />
              </svg>
              <div className="scoreRingContent">
                <strong className="scoreHeroNum">{correct}</strong>
                <span className="scoreHeroOf">/ {graded}</span>
              </div>
            </div>
            <div className="scoreHeroPct">{pct}%</div>
          </div>

          {/* 3. Stats row */}
          <div className="statsRow">
            <div className="statCard statCard--correct">
              <span className="statCardIcon">✓</span>
              <div className="statCardBody">
                <span className="statCardLabel">Correct</span>
                <strong className="statCardValue">{correct}</strong>
              </div>
            </div>
            <div className="statCard statCard--incorrect">
              <span className="statCardIcon">✕</span>
              <div className="statCardBody">
                <span className="statCardLabel">Incorrect</span>
                <strong className="statCardValue">{wrong}</strong>
              </div>
            </div>
            <div className="statCard statCard--accuracy">
              <span className="statCardIcon">%</span>
              <div className="statCardBody">
                <span className="statCardLabel">Accuracy</span>
                <strong className="statCardValue">{accuracy}%</strong>
              </div>
            </div>
            {uncertain > 0 && (
              <div className="statCard statCard--uncertain">
                <span className="statCardIcon">?</span>
                <div className="statCardBody">
                  <span className="statCardLabel">Uncertain</span>
                  <strong className="statCardValue">{uncertain}</strong>
                </div>
              </div>
            )}
          </div>

          {uncertain > 0 && (
            <p className="resultsNote">
              Percentage excludes {uncertain} uncertain answer{uncertain > 1 ? 's' : ''}.
            </p>
          )}

          {/* 4. Feedback panel with Scout */}
          <div className="feedbackPanel">
            <img
              className="feedbackScout"
              src={scoutState.imageSrc}
              alt="Scout reaction"
              draggable="false"
            />
            <div className="feedbackContent">
              <div className="feedbackHeadline">{scoutState.headline}</div>
              <div className="feedbackMessage">{scoutState.message}</div>
            </div>
          </div>

          {/* 5. Action buttons */}
          <div className="resultsActions">
            <button className="ui-btn ui-btn--primary resultsActionPrimary" onClick={() => setMode('review')}>
              <svg className="btnIcon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 4.5h10M3 8h7M3 11.5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              Review Answers
            </button>
            <button className="ui-btn ui-btn--ghost resultsActionSecondary" onClick={handleNewQuiz}>
              <svg className="btnIcon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M13.5 8A5.5 5.5 0 1 1 8 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M8 2.5V5l2-1.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              New Quiz
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderReview = () => {
    const questions = quizResult?.questions ?? [];

    return (
      <div className="quiz-review">
        <div className="quiz-session-header">
          <div>
            <h3>Review</h3>
            <p className="results-meta">See what you got right and wrong</p>
          </div>
          <div className="quiz-session-actions">
            <button className="ui-btn ui-btn--neutral" onClick={() => setMode('summary')}>
              Back to Summary
            </button>
            <button className="ui-btn ui-btn--neutral" onClick={handleNewQuiz}>
              New Quiz
            </button>
          </div>
        </div>

        <div className="quiz-questions-list">
          {questions.map((rawQ, idx) => {
            const q = normalizeQuestion(rawQ);
            const ua = answersByIndex[idx];
            const verdict = verdictsByIndex[idx];
            const isCorrect = verdict?.verdict === 'correct';
            const isWrong = verdict?.verdict === 'wrong';
            const isUncertain = verdict?.verdict === 'uncertain';

            // Resolve letter answer (A/B/C/D) to full choice text
            let displayAnswer = ua || '—';
            if (ua && q.choices?.length && /^[A-Z]$/.test(ua)) {
              const choiceIdx = ua.charCodeAt(0) - 65;
              if (choiceIdx >= 0 && choiceIdx < q.choices.length) {
                displayAnswer = q.choices[choiceIdx];
              }
            }

            return (
              <div key={idx} className="reviewCard">
                {/* Header row: number + badges */}
                <div className="reviewHeader">
                  <span className="reviewNumber">Question {idx + 1}</span>
                  <div className="reviewBadges">
                    <span className="reviewTypeBadge">{q.question_type.replace('_', ' ')}</span>
                    {isCorrect && <span className="reviewStatusBadge reviewStatusBadge--correct">Correct</span>}
                    {isWrong && <span className="reviewStatusBadge reviewStatusBadge--wrong">✕ Incorrect</span>}
                    {isUncertain && <span className="reviewStatusBadge reviewStatusBadge--uncertain">? Uncertain</span>}
                  </div>
                </div>

                {/* Question text – full width */}
                <div className="reviewQuestionText">{q.question_text}</div>

                {/* Answer stack – vertical */}
                <div className={`reviewAnswerBlock ${isWrong ? 'reviewAnswerBlock--wrong' : ''} ${isCorrect ? 'reviewAnswerBlock--yours-correct' : ''}`}>
                  <span className="reviewAnswerLabel">
                    {isWrong && <svg className="reviewLabelIcon" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>}
                    {isCorrect && <svg className="reviewLabelIcon" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 6.5L5 9l4.5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    Your Answer
                  </span>
                  <div className="reviewAnswerValue">{displayAnswer}</div>
                </div>
                <div className={`reviewAnswerBlock ${(!isUncertain && !isCorrect) ? 'reviewAnswerBlock--correct' : ''} ${isCorrect ? 'reviewAnswerBlock--yours-correct' : ''}`}>
                  <span className="reviewAnswerLabel">
                    <svg className="reviewLabelIcon" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2.5 6.5L5 9l4.5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    Correct Answer
                  </span>
                  <div className="reviewAnswerValue">{q.correct_answer ?? '—'}</div>
                </div>

                {verdict?.confidence !== undefined && q.question_type === 'short_answer' && (
                  <div className="reviewConfidence">
                    Confidence: {Math.round(verdict.confidence * 100)}%
                  </div>
                )}

                {/* Explanation panel */}
                {q.explanation && (
                  <div className="reviewExplanation">
                    <div className="reviewExplanationHeader">
                      <svg className="reviewExplanationIcon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/><path d="M8 7v4M8 5h.007" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      <span className="reviewExplanationTitle">Explanation</span>
                    </div>
                    <p className="reviewExplanationBody">{q.explanation}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* End-of-review actions */}
        <div className="reviewEndSection">
          <div className="reviewEndDivider" />
          <p className="reviewEndLabel">End of review</p>
          <div className="reviewEndActions">
            <button className="ui-btn ui-btn--neutral" onClick={() => setMode('summary')}>
              Back to Summary
            </button>
            <button className="ui-btn ui-btn--neutral" onClick={handleNewQuiz}>
              New Quiz
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============ MAIN RENDER ============
  // NOTE: We no longer block the entire tool for non-premium users.
  // History viewing is always allowed; only new creation is gated via
  // handleGenerateQuiz → isCreationBlocked → showUpgradeModal.
  
  // ============ LAYOUT RENDERING ============
  // EXPANDED MODE: 2-column layout (history left + workspace right)
  if (isPanelExpanded) {
    return (
      <div className="quiz-tool quiz-tool--expanded" ref={containerRef}>
        <div className="quiz-tool-layout quiz-tool-layout--expanded">
          {/* LEFT COLUMN: Quiz History Panel (no + New button in expanded) */}
          <div className="quiz-tool-sidebar">
            <QuizHistoryPanel
              activeQuizId={selectedQuizForDetail?.id || quizResult?.quiz_id}
              onSelectQuiz={handleSelectQuizFromPanel}
              onNewQuiz={handleNewQuiz}
              showNewButton={false}
            />
          </div>

          {/* RIGHT COLUMN: Workspace (Detail, Builder, Taking, Summary, Review) */}
          <div className="quiz-tool-workspace">
            {/* Loading state - fills workspace */}
            {loading && (
              <div className="quiz-loading-state quiz-loading-state--expanded">
                {renderLoadingState()}
              </div>
            )}

            {/* Detail view - show when quiz selected from list but not actively taking */}
            {!loading && !quizResult && selectedQuizForDetail && (
              <QuizDetailView
                key={selectedQuizForDetail.id}
                quiz={selectedQuizForDetail}
                onStartQuiz={handleStartQuizFromDetail}
                onSelectAttempt={handleSelectAttemptFromDetail}
                onBack={() => setSelectedQuizForDetail(null)}
              />
            )}

            {/* Setup/Builder mode - always show builder when no active quiz and no detail */}
            {!loading && !quizResult && !selectedQuizForDetail && (
              <div className="quizzesExpandedLayout">
                {/* Left Column: Scout Hero */}
                <div className="quizzesScoutHero">
                  <img 
                    src="/scout_images/scout_quizzes.png" 
                    alt="Scout Quizzes"
                    className="quizzesScoutHero__img"
                  />
                </div>

                {/* Right Column: Create Quiz Panel */}
                <div className="createQuizPanel">
                  {/* Panel Header */}
                  <div className="createQuizPanelHeader">
                    <h3 className="createQuizPanelHeader__title">Create New Quiz</h3>
                    <p className="createQuizPanelHeader__subtitle">
                      Test your knowledge with AI-generated quizzes based on your course documents.
                    </p>
                    
                    {/* Documents Control - centered when no docs selected */}
                    {selectedDocumentIds.length === 0 && (
                      <div className="createQuizPanelHeader__docs">
                        <button
                          className="ui-btn ui-btn--primary selectDocumentsBtn"
                          onClick={() => { if (isCreationBlocked) { setShowUpgradeModal(true); return; } setShowDocModal(true); }}
                        >
                          📄 Select Documents
                        </button>
                        <p className="createQuizPanelHeader__hint">Select documents to generate a quiz</p>
                      </div>
                    )}
                  </div>

                  {/* Panel Controls */}
                  <div className="createQuizPanelControls">
                    {/* Documents Summary - shown when docs selected */}
                    {selectedDocumentIds.length > 0 && (
                      <div className="doc-status" onClick={() => { if (isCreationBlocked) { setShowUpgradeModal(true); return; } setShowDocModal(true); }}>
                        <div className="doc-status__badge">
                          <span className="doc-status__icon">📄</span>
                          <span className="doc-status__count">{selectedDocumentIds.length}</span>
                          <span className="doc-status__label">Document{selectedDocumentIds.length !== 1 ? 's' : ''} Selected</span>
                        </div>
                        <button className="doc-status__action">Change</button>
                      </div>
                    )}
                    <div className="quiz-divider" />

                    <div className="quiz-form-section">
                      <span className="control-label">Questions</span>
                      <input
                        id="num-questions"
                        type="range"
                        min="5"
                        max="50"
                        step="5"
                        value={numQuestions}
                        onChange={(e) => setNumQuestions(Number(e.target.value))}
                        className="quiz-slider"
                      />
                      <span className="control-value-below">{numQuestions} questions</span>
                    </div>

                    <div className="quiz-divider" />

                    <div className="quiz-form-section">
                      <div className="quiz-options-grid">
                        <div className="difficulty-group">
                          <div className="section-label">Difficulty</div>
                          {['beginner', 'intermediate', 'advanced'].map(level => (
                            <label key={level} className="radio-option">
                              <input
                                type="radio"
                                name="difficulty-expanded"
                                value={level}
                                checked={difficultyLevel === level}
                                onChange={() => setDifficultyLevel(level)}
                              />
                              {level.charAt(0).toUpperCase() + level.slice(1)}
                            </label>
                          ))}
                        </div>

                        <div className="question-type-group">
                          <div className="section-label">Question Types</div>
                          <span className="section-hint">Select one or more</span>
                          {(() => {
                            const sel = new Set(questionTypePreset.split(','));
                            const toggle = (key) => {
                              const next = new Set(sel);
                              if (next.has(key)) next.delete(key); else next.add(key);
                              if (next.size === 0) return;
                              setQuestionTypePreset([...next].join(','));
                            };
                            return [
                              { value: 'mc', label: 'Multiple Choice' },
                              { value: 'tf', label: 'True / False' },
                              { value: 'sa', label: 'Short Answer' }
                            ].map(type => (
                              <label key={type.value} className="checkbox-option">
                                <input
                                  type="checkbox"
                                  checked={sel.has(type.value)}
                                  onChange={() => toggle(type.value)}
                                />
                                {type.label}
                              </label>
                            ));
                          })()}
                        </div>
                      </div>
                    </div>

                    <div className="quiz-divider" />

                    <div className="quiz-form-section quiz-form-section--cta">
                      <button
                        className="quiz-generate-btn"
                        onClick={handleGenerateQuiz}
                        disabled={selectedDocumentIds.length === 0 || loading}
                      >
                        {loading ? 'Generating...' : '✨ Generate Quiz'}
                      </button>
                      {selectedDocumentIds.length === 0 && (
                        <span className="quiz-generate-hint">Select documents to enable quiz generation</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Taking mode - active quiz in progress */}
            {!loading && !isGrading && quizResult && mode === 'taking' && renderTaking()}

            {/* Grading spinner - shown while AI grades text answers */}
            {isGrading && (
              <div className="quiz-grading-overlay">
                <div className="loading-spinner" />
                <p className="quiz-grading-label">Checking Answers…</p>
              </div>
            )}

            {/* Summary mode - quiz completed */}
            {!loading && !isGrading && quizResult && mode === 'summary' && renderSummary()}

            {/* Review mode - review answers */}
            {!loading && !isGrading && quizResult && mode === 'review' && renderReview()}
          </div>
        </div>

        {/* Document Selection Modal */}
        {showDocModal && (
          <DocumentSelectionModal
            isOpen={showDocModal}
            selectedDocuments={selectedDocumentIds}
            onToggleDocument={handleToggleDocument}
            onConnectDocuments={handleConnectDocuments}
            onClose={() => setShowDocModal(false)}
            requiredStatusIds={requiredStatusIds}
            title="Select Documents for Quizzes"
            description="Choose processed documents to generate quiz questions from."
          />
        )}

        {/* Paywall modal – must be in EVERY return path */}
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentTier={currentTier}
          requiredTier="basic"
          featureName="Quiz Generator"
          featureDescription="Create AI-powered quizzes from your course documents and materials."
          triggerSource="quiz"
        />
      </div>
    );
  }

  // COLLAPSED MODE: Overlay approach - history always visible, create card on top
  return (
    <div className="quiz-tool quiz-tool--collapsed quizzes-collapsed-root" ref={containerRef}>
      {/* Header Row */}
      <div className="quiz-tool-header-row quizzes-header">
        <h2 className="quiz-tool-header-title">Quizzes</h2>
        
        <div className="quiz-tool-header-actions">
          <input
            type="text"
            placeholder="Search..."
            className="quiz-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          
          <button
            className="ui-btn ui-btn--primary ui-btn--icon"
            onClick={() => {
              setIsCreating(true);
              handleNewQuiz();
            }}
            title="Create New Quiz"
          >
            +
          </button>
        </div>
      </div>

      {/* Body - Contains overlay and history list */}
      <div 
        className="quiz-tool-body quizzes-body"
        style={{ '--create-overlay-offset': `${overlayHeight + 12}px` }}
      >
        {/* Loading State Overlay - fills entire panel */}
        {loading && (
          <div className="quiz-loading-overlay quiz-loading-overlay--collapsed">
            {renderLoadingState(true)}
          </div>
        )}

        {/* Create Quiz Overlay - absolute positioned on top */}
        <div 
          ref={overlayRef}
          className={`create-overlay ${isCreating && !quizResult ? 'create-overlay--open' : ''}`}
        >
          {isCreating && !quizResult && (
            <div className="create-overlay-card">
              <div className="create-overlay-header">
                <h3>Create New Quiz</h3>
                <button 
                  className="ui-btn ui-btn--icon create-overlay-close"
                  onClick={() => {
                    setIsCreating(false);
                    setLoading(false);
                    setError(null);
                  }}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              
              <div className="create-overlay-content">
                <QuizBuilderPanel
                  selectedDocumentIds={selectedDocumentIds}
                  numQuestions={numQuestions}
                  difficultyLevel={difficultyLevel}
                  questionTypePreset={questionTypePreset}
                  loading={loading}
                  error={error}
                  onSetShowDocModal={(val) => { if (val && isCreationBlocked) { setShowUpgradeModal(true); return; } setShowDocModal(val); }}
                  onSetNumQuestions={setNumQuestions}
                  onSetDifficultyLevel={setDifficultyLevel}
                  onSetQuestionTypePreset={setQuestionTypePreset}
                  onGenerateQuiz={handleGenerateQuiz}
                  density="compact"
                />
              </div>
            </div>
          )}
        </div>

        {/* Quiz History Scroll - always rendered, pushed down by overlay */}
        <div className="quiz-history-scroll" ref={listContainerRef}>
          {quizResult && !loading ? (
            /* Active Quiz View */
            <div className="quiz-active-view">
              {mode === 'taking' && renderTaking()}
              {mode === 'summary' && renderSummary()}
              {mode === 'review' && renderReview()}
            </div>
          ) : (
            <>
              {/* Hero Strip - transparent area above history */}
              {!isCreating && (
                <div className="quiz-hero-strip">
                  <img 
                    src="/scout_images/scout_quizzes.png" 
                    alt="Scout Quizzes"
                    className="quiz-hero-strip__image"
                  />
                </div>
              )}
              
              {/* Quiz History List */}
              <QuizHistoryPanel
                activeQuizId={null}
                onSelectQuiz={handleSelectQuizFromPanel}
                onNewQuiz={() => {
                  setIsCreating(true);
                  handleNewQuiz();
                }}
                hideHeader={true}
                hideSearch={true}
                showSectionHeader={true}
              />
            </>
          )}
        </div>
      </div>

      {/* Document Selection Modal */}
      {showDocModal && (
        <DocumentSelectionModal
          isOpen={showDocModal}
          selectedDocuments={selectedDocumentIds}
          onToggleDocument={handleToggleDocument}
          onConnectDocuments={handleConnectDocuments}
          onClose={() => setShowDocModal(false)}
          requiredStatusIds={requiredStatusIds}
          title="Select Documents for Quizzes"
          description="Choose processed documents to generate quiz questions from."
        />
      )}

      {/* Paywall modal – shown when non-entitled user tries to generate */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={currentTier}
        requiredTier="basic"
        featureName="Quiz Generator"
        featureDescription="Create AI-powered quizzes from your course documents and materials."
        triggerSource="quiz"
      />
    </div>
  );
}
